import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Info, X, Loader } from 'lucide-react';

interface ToastProps {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info' | 'loading';
  title: string;
  description?: string;
  duration?: number;
  onClose: (id: string) => void;
}

interface ToastState {
  toasts: ToastProps[];
}

class ToastManager {
  private listeners: ((toasts: ToastProps[]) => void)[] = [];
  private toasts: ToastProps[] = [];

  subscribe(listener: (toasts: ToastProps[]) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notify() {
    this.listeners.forEach(listener => listener([...this.toasts]));
  }

  private addToast(toast: Omit<ToastProps, 'onClose'>) {
    const newToast: ToastProps = {
      ...toast,
      onClose: this.removeToast.bind(this)
    };

    // Remove existing toast with same id
    this.toasts = this.toasts.filter(t => t.id !== toast.id);
    
    // Add new toast
    this.toasts.push(newToast);
    this.notify();

    // Auto remove after duration (except loading toasts)
    if (toast.type !== 'loading' && toast.duration !== 0) {
      setTimeout(() => {
        this.removeToast(toast.id);
      }, toast.duration || 4000);
    }
  }

  private removeToast(id: string) {
    this.toasts = this.toasts.filter(t => t.id !== id);
    this.notify();
  }

  success(title: string, options?: { description?: string; duration?: number; id?: string }) {
    this.addToast({
      id: options?.id || Date.now().toString(),
      type: 'success',
      title,
      description: options?.description,
      duration: options?.duration
    });
  }

  error(title: string, options?: { description?: string; duration?: number; id?: string }) {
    this.addToast({
      id: options?.id || Date.now().toString(),
      type: 'error',
      title,
      description: options?.description,
      duration: options?.duration || 6000
    });
  }

  warning(title: string, options?: { description?: string; duration?: number; id?: string }) {
    this.addToast({
      id: options?.id || Date.now().toString(),
      type: 'warning',
      title,
      description: options?.description,
      duration: options?.duration
    });
  }

  info(title: string, options?: { description?: string; duration?: number; id?: string }) {
    this.addToast({
      id: options?.id || Date.now().toString(),
      type: 'info',
      title,
      description: options?.description,
      duration: options?.duration
    });
  }

  loading(title: string, options?: { description?: string; id?: string }) {
    this.addToast({
      id: options?.id || Date.now().toString(),
      type: 'loading',
      title,
      description: options?.description,
      duration: 0 // Loading toasts don't auto-dismiss
    });
  }
}

export const toast = new ToastManager();

const Toast: React.FC<ToastProps> = ({ id, type, title, description, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(id), 300);
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-600" />;
      case 'loading':
        return <Loader className="w-5 h-5 text-blue-600 animate-spin" />;
      default:
        return <Info className="w-5 h-5 text-gray-600" />;
    }
  };

  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      case 'loading':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div
      className={`transform transition-all duration-300 ease-in-out ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}
    >
      <div className={`max-w-sm w-full ${getBackgroundColor()} border rounded-lg shadow-lg p-4`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium text-gray-900">{title}</p>
            {description && (
              <p className="mt-1 text-sm text-gray-600">{description}</p>
            )}
          </div>
          {type !== 'loading' && (
            <div className="ml-4 flex-shrink-0">
              <button
                onClick={handleClose}
                className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  useEffect(() => {
    const unsubscribe = toast.subscribe(setToasts);
    return unsubscribe;
  }, []);

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {toasts.map((toastProps) => (
        <Toast key={toastProps.id} {...toastProps} />
      ))}
    </div>
  );
};